import { z } from "zod";

import {
  CUSTOM_GAP,
  CUSTOM_GAP_SIZE_ATTR,
  CUSTOM_GAP_SIZE_MAX,
  readCustomGapSize,
} from "./custom-gap";

/**
 * The `<tc-carousel>` settings vocabulary.
 *
 * Four places must agree on these: the GrapesJS traits an operator edits, the
 * runtime controller that reads them off the element, the storefront renderer
 * that emits an initial state before the runtime loads, and the copilot's emit
 * contract in `interactive-blocks.ts`. A second copy of the attribute names is
 * exactly the drift this module prevents, so it lives here rather than beside
 * the component — `@tripcart/page-builder` and the render paths cannot import
 * each other.
 *
 * Settings are plain HTML attributes, never a JSON `data-*` payload. A JSON
 * blob would put any URL inside it past the document guard, which scheme-checks
 * a fixed list of real URL attributes and nothing else.
 */

/**
 * Slides visible at the widest breakpoint. Narrow screens step this down in
 * CSS, so the stored value is the desktop maximum rather than a fixed count.
 *
 * `1` was excluded at first, on the reasoning that a single-slide slideshow
 * hides content behind a control and so needs APG carousel semantics this
 * block was not going to carry. That reasoning had a hole from the start: the
 * media queries already clamp to `1` below 640px, so every carousel has always
 * been a single-slide slideshow on a phone. The runtime now derives those
 * semantics from the COMPUTED value instead (see `#describeTrack`), which
 * covers the phone clamp, a stored `1`, and the `fade` effect alike — so `1`
 * is simply an option now.
 */
export const CAROUSEL_PER_VIEW = [1, 2, 3, 4, 5] as const;
export type CarouselPerView = (typeof CAROUSEL_PER_VIEW)[number];

export const DEFAULT_PER_VIEW: CarouselPerView = 3;

/**
 * Per view on a tablet and a phone (`per-view-tablet`, `per-view-mobile`,
 * `device-attrs.ts`). Unset, the media queries draw what every carousel drew
 * before the settings existed: 2 up to 1024px and 1 up to 640px. Nothing in
 * the runtime reads these: like `per-view` they are CSS, and the one reader
 * of the result is `carousel-aria.ts`, which reads the computed value.
 */
export const CAROUSEL_TABLET_PER_VIEW = [1, 2, 3] as const;
export const DEFAULT_TABLET_PER_VIEW = 2;
export const CAROUSEL_MOBILE_PER_VIEW = [1, 2] as const;
export const DEFAULT_MOBILE_PER_VIEW = 1;

/** The CSS custom property the stored `perView` resolves through. */
export const PER_VIEW_CSS_VAR = "--tc-carousel-per-view";

/**
 * How the pagination dots are drawn. Appearance only — the NUMBER of them is
 * the engine's snap list and nothing here can change it.
 *
 * A shape rather than a glyph, and that is the whole reason this exists. Dots
 * are uniform by definition, so there is no per-dot choice to make, and they
 * cannot be authored components the way the arrows are: their count changes at
 * runtime with the slide count, with `loop`, and with `perView` — which is CSS,
 * so crossing a breakpoint genuinely changes it. A stored list of dots would be
 * wrong on the first phone that loaded the page.
 *
 * `circle` is the default and deliberately matches NO rule of its own; it is
 * the shape the base styling already draws.
 *
 * `dash` is the one that varies by STATE rather than only by shape: the
 * current dot stretches into a pill while the rest stay circles, so a visitor
 * can read position from the shape and not only from the colour — which is the
 * part of the default row a colour-blind visitor cannot use.
 */
export const CAROUSEL_DOT_SHAPES = [
  "circle",
  "dash",
  "square",
  "ring",
] as const;
export type CarouselDotShape = (typeof CAROUSEL_DOT_SHAPES)[number];

export const DEFAULT_DOT_SHAPE: CarouselDotShape = "circle";

/**
 * How big the dots are drawn. Appearance only, like the shape, and resolved in
 * pure CSS through one custom property — every shape derives its measurements
 * from it, so a size works the same whichever shape is picked.
 *
 * `small` is the size the block has always drawn, so it is the default and an
 * existing carousel looks identical after this shipped.
 *
 * The larger two were added because `dash` used to paint at HALF the diameter
 * of the other shapes, so a dash row read as slight beside a row of filled
 * circles. That was a bug in the dash rule rather than a fact about dashes,
 * and it is fixed — every shape now paints its whole button. These sizes stay
 * because a row of dots can want to be bigger on its own account, not as a
 * correction for one shape.
 */
export const CAROUSEL_DOT_SIZES = ["small", "medium", "large"] as const;
export type CarouselDotSize = (typeof CAROUSEL_DOT_SIZES)[number];

export const DEFAULT_DOT_SIZE: CarouselDotSize = "small";

/**
 * How a slide behaves as it moves through the viewport. ONE at a time, on
 * purpose — these are not independent switches.
 *
 * `fade` replaces the sliding motion outright (Embla's Fade plugin stops
 * translating the row and cross-fades the slides in place), so it cannot be
 * combined with anything that assumes the row slides. And `fade` drives slide
 * OPACITY, which is exactly what the `opacity` effect drives, so the two would
 * write the same property from two places. A single-select makes both
 * conflicts unrepresentable rather than merely discouraged.
 *
 * `fade` also OVERRIDES `perView`, and is the only setting here that overrides
 * another. The plugin shows one slide and parks the rest off-screen, so a row
 * still sized for three renders as one narrow slide with the others adrift.
 * The block's CSS forces a full-width slide under `fade` for that reason, and
 * the runtime escalates to APG carousel semantics with it, because one slide
 * at a time means the others are genuinely hidden behind a control.
 *
 * `slide` is the default and is the plain behaviour the block has always had.
 * The rest are progressive: each needs JavaScript, and reduced motion turns the
 * tweens off, so a page is never broken by one being unavailable.
 */
export const CAROUSEL_EFFECTS = ["slide", "fade", "scale", "opacity"] as const;
export type CarouselEffect = (typeof CAROUSEL_EFFECTS)[number];

export const DEFAULT_EFFECT: CarouselEffect = "slide";

/**
 * Whether the row's height follows the slide on show, rather than standing at
 * the tallest slide's height for every snap.
 *
 * OFF by default, and that is not only for compatibility. A flex row stretches
 * every slide to the tallest, which is what a row of cards wants — equal-height
 * cards with their backgrounds meeting. Auto height earns its keep when ONE
 * slide shows at a time and the slides differ a lot in length, where the
 * reserved space under a short slide is dead space. With several slides in
 * view it means "the tallest slide currently visible", so the row jumps as the
 * visible set changes.
 *
 * Embla's plugin drives it, and the plugin snapshots every slide's height at
 * init. Nothing in Embla refreshes that: its resize handler measures a
 * horizontal carousel's WIDTH only (`Axis.measureSize` returns `height` solely
 * on the `y` axis), so a slide growing taller never re-initialises it. The
 * runtime therefore watches slide height itself and re-initialises the engine,
 * which is what keeps a late-loading image or an operator's typing from
 * leaving the row clipped at a stale height.
 */

/**
 * The two spacing settings, on one named scale rather than a free length.
 *
 * A scale for the same reason the dot SIZE is one: the block resolves both
 * through custom properties that feed measurements Embla reads, so a value has
 * to be one the layout was designed around. `none` is offered for the gap
 * because a row of full-bleed photographs is a real design; it is offered for
 * the arrow inset too, which puts the arrows flush against the slide edges.
 * The arrow inset has one more option, `outside` (`CAROUSEL_ARROW_INSETS`).
 *
 * `medium` is what the block has always drawn, so it is the default for both
 * and an existing carousel is unchanged.
 *
 * THE GAP CHANGES A VALUE, NEVER THE MODEL. Spacing stays a margin on every
 * slide but the first, with a trailing one under `loop` — the arrangement the
 * block measured three ways to arrive at. Only the length moves.
 */
export const CAROUSEL_SPACINGS = ["none", "small", "medium", "large"] as const;
export type CarouselSpacing = (typeof CAROUSEL_SPACINGS)[number];

/**
 * The arrow inset's options: the scale, which moves an arrow IN from the
 * slide row's edge, plus `outside`, which centres it on that edge so half of
 * it sits beside the slides and it covers no card's text. On a phone,
 * `outside` moves the arrows back in: the page's side gutter is narrower
 * than half an arrow (carousel-arrow-inset-css.ts).
 */
export const CAROUSEL_ARROW_INSETS = [...CAROUSEL_SPACINGS, "outside"] as const;
export type CarouselArrowInset = (typeof CAROUSEL_ARROW_INSETS)[number];

/** What an absent `arrow-inset` means: the inset every carousel saved
 *  before `outside` existed was drawn with. */
export const DEFAULT_ARROW_INSET: CarouselArrowInset = "medium";

/** What a newly dropped Carousel is given. Written onto the drop, never
 *  read from an absent attribute, so a saved page keeps its look. */
export const DROPPED_ARROW_INSET: CarouselArrowInset = "outside";

/**
 * The gap's options: the named scale, plus `custom`, which takes its length
 * from `gap-size` in whole pixels (custom-gap.ts). Only the gap has it. The
 * arrow inset stays on the scale.
 *
 * A free length is still a LENGTH, never a new spacing model: it resolves
 * through the same `--tc-carousel-gap` the named options set, into the same
 * margin between slides. `medium`'s 16px is where a custom gap starts.
 */
export const CAROUSEL_GAPS = [...CAROUSEL_SPACINGS, CUSTOM_GAP] as const;
export type CarouselGap = (typeof CAROUSEL_GAPS)[number];

export const DEFAULT_GAP: CarouselGap = "medium";
export const DEFAULT_GAP_SIZE = 16;

/**
 * How far one press of an arrow moves the row.
 *
 * `one` is a single slide. `page` hands Embla `slidesToScroll: "auto"`, which
 * groups the slides by what actually fits in the viewport — NOT by the stored
 * `perView`. That distinction is the whole reason this is two words rather
 * than a number: `perView` is the desktop maximum and the media queries clamp
 * it, so paging by the stored 3 would jump three slides on a phone showing
 * one. `auto` measures, so it follows the clamp for free.
 *
 * The dots follow either way, because their count is the engine's snap list
 * and grouping changes that list.
 */
export const CAROUSEL_STEPS = ["one", "page"] as const;
export type CarouselStep = (typeof CAROUSEL_STEPS)[number];

export const DEFAULT_STEP: CarouselStep = "one";

/**
 * Where the slide the engine stopped on sits in the track.
 *
 * `start` is the block's original behaviour and stays the default. `center`
 * is the look a peeking carousel usually wants, with a neighbour showing on
 * both sides rather than only on the trailing edge.
 *
 * `end` is deliberately NOT offered. Embla accepts it, but it reads as a
 * right-aligned row that still scrolls left to right, and the first snap is
 * clamped flush by `containScroll` anyway — so most of the block would look
 * identical and the rest would look broken.
 */
export const CAROUSEL_ALIGNMENTS = ["start", "center"] as const;
export type CarouselAlignment = (typeof CAROUSEL_ALIGNMENTS)[number];

export const DEFAULT_ALIGNMENT: CarouselAlignment = "start";

/**
 * How the block moves when nobody is touching it. THREE-WAY AND NOT TWO
 * SWITCHES, because the two moving modes are different Embla plugins that
 * drive the same engine: `step` advances a snap at a time on a timer, and
 * `continuous` glides without stopping. Run together they fight over the
 * scroll position, so a select is what makes that impossible rather than
 * merely discouraged.
 *
 * Both moving modes owe a stop control — see `requiresRotationControl`.
 */
export const CAROUSEL_MOTIONS = ["off", "step", "continuous"] as const;

export type CarouselMotion = (typeof CAROUSEL_MOTIONS)[number];

export const DEFAULT_MOTION: CarouselMotion = "off";

/**
 * How wide each slide is. `equal` (the default, and what an absent attribute
 * means) divides the row by Per view. `variable` sizes each slide to its own
 * content, up to most of the row — Embla's "variable widths" example, which
 * needs no engine option because Embla measures whatever widths it finds.
 */
export const CAROUSEL_SLIDE_WIDTHS = ["equal", "variable"] as const;
export type CarouselSlideWidth = (typeof CAROUSEL_SLIDE_WIDTHS)[number];

/** Attribute names, as written in the document and by the copilot. */
export const CAROUSEL_ATTRS = {
  label: "label",
  perView: "per-view",
  perViewTablet: "per-view-tablet",
  perViewMobile: "per-view-mobile",
  slideWidth: "slide-width",
  peek: "peek",
  gap: "gap",
  gapSize: CUSTOM_GAP_SIZE_ATTR,
  arrowInset: "arrow-inset",
  step: "step",
  align: "align",
  dots: "dots",
  thumbs: "thumbs",
  dotShape: "dot-shape",
  dotSize: "dot-size",
  effect: "effect",
  autoHeight: "auto-height",
  loop: "loop",
  motion: "motion",
} as const;

/**
 * The rotation control's accessible name, in its two states.
 *
 * FIXED, not a setting. The button is an ICON now, so there is no visible text
 * for an operator to write — the name exists only for a screen reader, and the
 * pair of them is what APG's carousel example varies to announce the state
 * (it uses `aria-label` and never `aria-pressed`). Two text fields for strings
 * nobody sees on the page were more panel than the choice was worth.
 *
 * ⚠ THE COST IS TRANSLATION. This is a multi-tenant storefront, and an agency
 * that does not serve English now has no way to change what a screen reader
 * announces here. The block has no route to a locale — the web component runs
 * in the storefront with no i18n — so closing that needs one, not another
 * attribute.
 *
 * The arrows are unaffected: they are authored components and carry their own
 * accessible names, which an operator edits by selecting the arrow.
 */
export const CAROUSEL_LABEL_DEFAULTS = {
  stop: "Stop slide rotation",
  start: "Start slide rotation",
} as const;

/**
 * Every boolean defaults to `false`, because an absent HTML boolean attribute
 * means false and `parseCarouselSettings` always supplies an explicit value —
 * a `.default(true)` here would be dead code that reads as a promise the parser
 * never keeps.
 *
 * Dots are nonetheless ON for a newly inserted carousel. That comes from the
 * block template and the copilot's manifest example writing the attribute out,
 * not from this schema, so "switched off" and "never set" stay the same thing.
 *
 * There is no `arrows` setting. The arrows are authored components, so their
 * presence in the markup IS the setting — a boolean here could disagree with
 * the DOM, and the one that disagreed would win somewhere.
 */
export const carouselSettingsSchema = z.object({
  /** Accessible name for the scroll region. A region with none is a known axe failure. */
  label: z.string().trim().default(""),
  perView: z
    .union([
      z.literal(1),
      z.literal(2),
      z.literal(3),
      z.literal(4),
      z.literal(5),
    ])
    .default(DEFAULT_PER_VIEW),
  peek: z.boolean().default(false),
  gap: z.enum(CAROUSEL_GAPS).default(DEFAULT_GAP),
  /** Pixels. Read only when `gap` is `custom`. */
  gapSize: z
    .number()
    .int()
    .min(0)
    .max(CUSTOM_GAP_SIZE_MAX)
    .default(DEFAULT_GAP_SIZE),
  arrowInset: z.enum(CAROUSEL_ARROW_INSETS).default(DEFAULT_ARROW_INSET),
  step: z.enum(CAROUSEL_STEPS).default(DEFAULT_STEP),
  align: z.enum(CAROUSEL_ALIGNMENTS).default(DEFAULT_ALIGNMENT),
  dots: z.boolean().default(false),
  /**
   * A strip of thumbnails under the slides, one per slide, drawn from each
   * slide's first `<img>`. Generated by the runtime like the dots, so it can
   * never disagree with the slides. Only the Lightbox panel offers it: a row
   * of text cards has nothing to show in a thumbnail.
   */
  thumbs: z.boolean().default(false),
  dotShape: z.enum(CAROUSEL_DOT_SHAPES).default(DEFAULT_DOT_SHAPE),
  dotSize: z.enum(CAROUSEL_DOT_SIZES).default(DEFAULT_DOT_SIZE),
  effect: z.enum(CAROUSEL_EFFECTS).default(DEFAULT_EFFECT),
  autoHeight: z.boolean().default(false),
  loop: z.boolean().default(false),
  motion: z.enum(CAROUSEL_MOTIONS).default(DEFAULT_MOTION),
});

export type CarouselSettings = z.infer<typeof carouselSettingsSchema>;

/**
 * HTML boolean-attribute semantics: presence means true. Only an absent value
 * is false, plus the two spellings a serializer round-trip can produce
 * (`false`, and the boolean itself when read from stored GrapesJS attributes
 * rather than from the DOM).
 */
const readBooleanAttr = (value: unknown): boolean => {
  if (value === undefined || value === null) return false;
  if (typeof value === "boolean") return value;
  return String(value).toLowerCase() !== "false";
};

const readDotShape = (value: unknown): CarouselDotShape | undefined =>
  (CAROUSEL_DOT_SHAPES as readonly string[]).includes(String(value))
    ? (value as CarouselDotShape)
    : undefined;

const readDotSize = (value: unknown): CarouselDotSize | undefined =>
  (CAROUSEL_DOT_SIZES as readonly string[]).includes(String(value))
    ? (value as CarouselDotSize)
    : undefined;

/** One reader for every named-option setting: in the list, or fall back. */
const readOneOf = <T extends string>(
  options: readonly T[],
  value: unknown,
): T | undefined =>
  (options as readonly string[]).includes(String(value))
    ? (value as T)
    : undefined;

const readEffect = (value: unknown): CarouselEffect | undefined =>
  (CAROUSEL_EFFECTS as readonly string[]).includes(String(value))
    ? (value as CarouselEffect)
    : undefined;

const readPerView = (value: unknown): CarouselPerView | undefined => {
  const n = Number(value);
  return (CAROUSEL_PER_VIEW as readonly number[]).includes(n)
    ? (n as CarouselPerView)
    : undefined;
};

/**
 * Parse stored or DOM attributes into settings, falling back to defaults for
 * anything missing or unrecognised. Never throws: an operator-authored
 * document is not a wire payload, and a bad `per-view` should degrade to the
 * default rather than fail a page render.
 */
export function parseCarouselSettings(
  attrs: Readonly<Record<string, unknown>>,
): CarouselSettings {
  const text = (name: string): string | undefined =>
    typeof attrs[name] === "string" && attrs[name].trim() !== ""
      ? attrs[name]
      : undefined;

  return carouselSettingsSchema.parse({
    label: text(CAROUSEL_ATTRS.label),
    perView: readPerView(attrs[CAROUSEL_ATTRS.perView]),
    peek: readBooleanAttr(attrs[CAROUSEL_ATTRS.peek]),
    gap: readOneOf(CAROUSEL_GAPS, attrs[CAROUSEL_ATTRS.gap]),
    gapSize: readCustomGapSize(attrs[CAROUSEL_ATTRS.gapSize]),
    arrowInset: readOneOf(
      CAROUSEL_ARROW_INSETS,
      attrs[CAROUSEL_ATTRS.arrowInset],
    ),
    step: readOneOf(CAROUSEL_STEPS, attrs[CAROUSEL_ATTRS.step]),
    align: readOneOf(CAROUSEL_ALIGNMENTS, attrs[CAROUSEL_ATTRS.align]),
    dotShape: readDotShape(attrs[CAROUSEL_ATTRS.dotShape]),
    dotSize: readDotSize(attrs[CAROUSEL_ATTRS.dotSize]),
    dots: readBooleanAttr(attrs[CAROUSEL_ATTRS.dots]),
    thumbs: readBooleanAttr(attrs[CAROUSEL_ATTRS.thumbs]),
    effect: readEffect(attrs[CAROUSEL_ATTRS.effect]),
    autoHeight: readBooleanAttr(attrs[CAROUSEL_ATTRS.autoHeight]),
    loop: readBooleanAttr(attrs[CAROUSEL_ATTRS.loop]),
    motion: readOneOf(CAROUSEL_MOTIONS, attrs[CAROUSEL_ATTRS.motion]),
  });
}

/**
 * Whether the block must render a stop/start control.
 *
 * WCAG 2.2.2 (Pause, Stop, Hide — Level A): anything that moves on its own for
 * more than five seconds needs a way to stop it. So this is derived from
 * `motion`, never a separate toggle an operator could switch off — and it
 * covers BOTH moving modes, because a continuous glide is if anything harder
 * to read past than a timed step.
 */
export const requiresRotationControl = (settings: CarouselSettings): boolean =>
  settings.motion !== "off";

/**
 * Whether the ENGINE wraps, which is the operator's `loop` plus one implication.
 *
 * Continuous motion wraps whatever the toggle says. Auto-scroll glides until it
 * runs out of track and then simply stops, with no rewind of its own — so
 * "Continuous" without `loop` is a carousel that moves once and sticks, which
 * is the exact complaint that replaced the hand-rolled timer. The operator's
 * stored `loop` is left untouched; this is a derived engine option, so turning
 * the mode back to Off or Step restores whatever they chose.
 */
export const carouselLoops = (settings: CarouselSettings): boolean =>
  settings.loop || settings.motion === "continuous";

/**
 * How long `step` rests on each slide, and how fast `continuous` glides.
 *
 * Neither is an operator setting. The panel offers the MODE, and pace is an
 * accessibility question rather than a taste one — too fast is unreadable, and
 * WCAG 2.2.2 starts counting at five seconds. Fixed here so both live in one
 * place with the reason attached.
 *
 * `CONTINUOUS_SCROLL_SPEED` is Embla's own unit (pixels of travel per frame at
 * 60fps, so 2 is roughly 120px a second) and `CONTINUOUS_START_DELAY_MS` is the
 * beat before it sets off, so a page does not arrive already in motion.
 */
export const AUTOPLAY_INTERVAL_MS = 5000;
export const CONTINUOUS_SCROLL_SPEED = 2;
export const CONTINUOUS_START_DELAY_MS = 1000;
